import { usePageTitle } from '@calendly/hooks'
import { I18n } from '@calendly/i18n'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { pickBy, some } from 'lodash-es'
import { useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { matchPath, useHistory } from 'react-router-dom'

import { useEventTypeContext } from 'features/event_type_provider'

import { etSidebarPaths, metSidebarPaths } from './constants'
import { MANAGED_EVENT_TYPE } from '../../constants/event_types'
import changeSet from '../../core/change_set'
import { useRightRail } from '../right_rail'
import { initializeEventTypeEditorSidebar, resetEventTypeEditorSidebar } from './slice'

export const usePageTitleWrapper = () => {
  const { eventType, isCreation } = useEventTypeContext()

  let pageTitle = 'Calendly'
  if (isCreation) {
    pageTitle = I18n.t('publisher.event_type_editor.title.creating')
  } else if (eventType) {
    pageTitle = I18n.t('publisher.event_type_editor.title.editing', { name: eventType.name })
  }

  usePageTitle(pageTitle)
}

export const useETEditorViewportOffset = () => {
  const isPhone = useDeviceType('isPhone')

  const headerHeight = 64
  const footerHeight = isPhone ? 120 : 74

  return { top: headerHeight, bottom: footerHeight }
}

const getDefaultOpenKey = (data) => {
  if (data.ownerType === 'User' || data.type === MANAGED_EVENT_TYPE) {
    return data.id ? 'availability' : 'location'
  }
  if (['Organization', 'Page'].includes(data.ownerType) && !data.id) {
    return 'hosts'
  }

  return null
}

const preparePaneProps = (paneProps) => pickBy({
  defaultOpenKey: getDefaultOpenKey(paneProps),
  ...paneProps
},
(value) => ['string', 'number', 'boolean'].includes(typeof value)
)

export const useEventTypeEditorSidebar = () => {
  const dispatch = useDispatch()
  const { openOrReplace, replace, close, current } = useRightRail()

  const openSidebar = useCallback(async (paneProps) => {
    if (current?.pane === 'single_use_link') {
      close({ rail: { name: 'single_use_link', state: current.paneState } })
    }

    openOrReplace('event_type_editor', preparePaneProps(paneProps))
    dispatch(initializeEventTypeEditorSidebar())
  }, [close, current, dispatch, openOrReplace])

  const closeSidebar = useCallback(async ({ resetState = true, resetAlert = false } = {}) => {
    // CRITICAL: Clear the right rail stack SYNCHRONOUSLY first to prevent useUrlSync race condition
    close({ rail: { name: 'event_type_editor', state: current.paneState } })
    // Then handle the cleanup
    dispatch(resetEventTypeEditorSidebar({ resetState, resetAlert }))
  }, [dispatch, close, current])

  const replaceSidebar = useCallback(async (paneProps) => {
    await dispatch(resetEventTypeEditorSidebar({ resetState: false }))
    replace('event_type_editor', preparePaneProps({ ...current?.paneProps, ...paneProps }))
    dispatch(initializeEventTypeEditorSidebar({ resetState: false }))
  }, [dispatch, replace, current])

  return {
    openSidebar,
    closeSidebar,
    replaceSidebar,
    isOpen: current?.pane === 'event_type_editor',
    ...current?.paneProps,
  }
}

export const useCloseOnNavigation = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { type } = useEventTypeEditorSidebar()
  const { clearStack } = useRightRail()
  const isMETParent = type === MANAGED_EVENT_TYPE

  useEffect(() => {
    // ET editor v3 may be open in the sidebar, we need to close it before switching to listed paths
    const unblock = history.block((location) => {
      // Close the sidebar if the user is navigating to a different page other than ET editor
      const isETEditorPage = some(etSidebarPaths, (path) => matchPath(location.pathname, path))
      const isMETEditorPage = some(metSidebarPaths, (path) => matchPath(location.pathname, path))

      const isNotOnProperPage = !(isETEditorPage || isMETEditorPage)
        || (isETEditorPage && isMETParent)
        || (isMETEditorPage && !isMETParent)

      if (isNotOnProperPage) {
        // If not confirmed, do nothing and stay on the current page
        if (!changeSet.confirmReset()) return false

        // Clear the right rail stack synchronously WITHOUT updating the URL
        // This prevents race conditions where history.replace() interferes with back navigation
        // The useUrlSync hook will properly clean up the URL params after navigation completes
        clearStack()
        dispatch(resetEventTypeEditorSidebar({ resetState: true, resetAlert: true }))
        unblock() // Allow navigation - original navigation will continue automatically
      }

      return true
    })

    return () => unblock()
  }, [clearStack, dispatch, history, isMETParent])
}
